/**
 * Utility functions for working with mapStyle objects
 */

// Loose shape that matches the maplibre StyleSpecification mutated throughout
// the codebase. Values are typed as `any` because the downstream JS-land
// consumers freely mix in source/layer variants beyond strict maplibre types.
export type MapStyleJS = {
  sources: Record<string, any>
  layers: any[]
  [key: string]: any
}

/**
 * Convert mapStyle to a mutable plain JS object
 * Deep clones to avoid mutating Redux state
 *
 * @param mapStyle - The mapStyle from Redux (plain object)
 * @returns A deep cloned plain JS object that can be safely mutated
 */
export const getMapStyleJS = (mapStyle: unknown): MapStyleJS | null => {
  if (!mapStyle) return null

  // Deep clone to avoid mutating Redux state
  return JSON.parse(JSON.stringify(mapStyle))
}

/**
 * Check if mapStyle is ready (non-null and has layers)
 */
export const isMapStyleReady = (mapStyle: unknown): boolean => {
  if (!mapStyle) return false
  return Array.isArray((mapStyle as { layers?: unknown[] })?.layers)
}

/**
 * Safely reads a MapLibre feature's geometry type without throwing.
 *
 * ArcGIS Vector Tile Services can encode features with geometry type 0 (UNKNOWN
 * per the MVT spec). MapLibre's `geometry` property is a lazy getter that throws
 * "unknown feature type: 0" when it encounters this — optional chaining (`?.`)
 * cannot intercept errors thrown *by* the getter, so a try/catch is required.
 */
export const safeGetGeometryType = (
  feature: { geometry?: { type?: string } } | null | undefined
): string | undefined => {
  try {
    return feature?.geometry?.type
  } catch {
    return undefined
  }
}
